import { useState } from "react";
import api from "@/lib/api";
import { Modal, formatCurrency, formatDate } from "@/components/pcp";
import { toast } from "sonner";
import { Plus, TrashSimple, FilePlus } from "@phosphor-icons/react";

const novaLinha = () => ({ contrato_item_id: "", codigo: "", descricao: "", categoria: "Outros", unidade: "un", qtd: "", preco_unitario: "" });

export function AditivosContrato({ contrato, onReload }) {
  const [modal, setModal] = useState(null); // null | { form }
  const aditivos = contrato.aditivos || [];
  const itens = contrato.itens || [];
  const nomeItem = (id) => { const i = itens.find((x) => x.id === id); return i ? `${i.codigo ? i.codigo + " · " : ""}${i.descricao}` : "?"; };

  const abrir = (ad) => setModal({
    id: ad?.id || null,
    numero: ad?.numero || String(aditivos.length + 1),
    data: ad?.data || new Date().toISOString().slice(0, 10),
    descricao: ad?.descricao || "",
    prazo_novo: ad?.prazo_novo || "",
    itens: ad ? ad.itens.map((l) => ({ ...novaLinha(), contrato_item_id: l.contrato_item_id, qtd: l.qtd, preco_unitario: l.preco_unitario })) : [novaLinha()],
  });
  const upd = (i, patch) => setModal((m) => ({ ...m, itens: m.itens.map((l, k) => (k === i ? { ...l, ...patch } : l)) }));
  const escolherItem = (i, id) => {
    const ci = itens.find((x) => x.id === id);
    upd(i, { contrato_item_id: id, preco_unitario: ci ? ci.preco_unitario : "", unidade: ci?.unidade || "un" });
  };
  const salvar = async () => {
    const linhas = modal.itens.filter((l) => parseFloat(l.qtd) > 0).map((l) => ({ ...l, contrato_item_id: l.contrato_item_id || null, qtd: parseFloat(l.qtd), preco_unitario: parseFloat(l.preco_unitario) || 0 }));
    if (!modal.numero) { toast.error("Informe o número do aditivo"); return; }
    if (!linhas.length) { toast.error("Informe ao menos um item com quantidade"); return; }
    if (linhas.some((l) => !l.contrato_item_id && !l.descricao)) { toast.error("Item novo precisa de descrição"); return; }
    try {
      const body = { numero: modal.numero, data: modal.data || null, descricao: modal.descricao, prazo_novo: modal.prazo_novo || null, itens: linhas };
      if (modal.id) await api.put(`/contratos/${contrato.id}/aditivos/${modal.id}`, body); else await api.post(`/contratos/${contrato.id}/aditivos`, body);
      toast.success(`Aditivo ${modal.numero} salvo`); setModal(null); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar aditivo"); }
  };
  const excluir = async (ad) => {
    if (!window.confirm(`Excluir o aditivo ${ad.numero}? As quantidades extras saem do saldo dos itens.`)) return;
    await api.delete(`/contratos/${contrato.id}/aditivos/${ad.id}`); toast.success("Aditivo excluído"); onReload();
  };
  const total = modal ? modal.itens.reduce((s, l) => s + (parseFloat(l.qtd) || 0) * (parseFloat(l.preco_unitario) || 0), 0) : 0;

  return (
    <div className="pcp-card" style={{ padding: 20, marginBottom: 20 }} data-testid="aditivos-contrato">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <h3 className="font-display font-bold" style={{ fontSize: "1.05rem" }}>Aditivos ({aditivos.length})</h3>
          <div style={{ fontSize: "0.75rem", color: "#71717A" }}>Quantidades extras e preços próprios. A medição consome primeiro o contrato principal e depois cada aditivo, na ordem.</div>
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => abrir(null)} data-testid="btn-novo-aditivo"><FilePlus size={14} weight="bold" /> Novo aditivo</button>
      </div>
      {aditivos.length === 0 ? <div style={{ color: "#71717A", fontSize: "0.85rem" }}>Nenhum aditivo registrado.</div> : (
        <table className="pcp-table">
          <thead><tr><th>Nº</th><th>Data</th><th>Descrição</th><th>Itens</th><th style={{ textAlign: "right" }}>Valor</th><th /></tr></thead>
          <tbody>
            {aditivos.map((ad) => (
              <tr key={ad.id} data-testid={`aditivo-row-${ad.numero}`}>
                <td className="font-mono font-semibold">{ad.numero}</td>
                <td className="font-mono text-xs">{formatDate(ad.data)}</td>
                <td>{ad.descricao || "—"}{ad.prazo_novo ? <span style={{ fontSize: "0.72rem", color: "#71717A" }}> · novo prazo {formatDate(ad.prazo_novo)}</span> : null}</td>
                <td style={{ fontSize: "0.78rem" }}>{ad.itens.map((l, k) => <div key={k}>{nomeItem(l.contrato_item_id)} · <strong>+{l.qtd.toLocaleString("pt-BR")}</strong> × {formatCurrency(l.preco_unitario)}</div>)}</td>
                <td className="font-mono font-semibold" style={{ textAlign: "right" }}>{formatCurrency(ad.valor)}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => abrir(ad)} data-testid={`btn-editar-aditivo-${ad.numero}`}>Editar</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: "#DC2626" }} onClick={() => excluir(ad)} data-testid={`btn-excluir-aditivo-${ad.numero}`}><TrashSimple size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <Modal open onClose={() => setModal(null)} title={modal.id ? `Editar aditivo ${modal.numero}` : "Novo aditivo"} size="xl">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr 1fr", gap: 12, marginBottom: 12 }}>
            <F label="Número *"><input className="input" value={modal.numero} onChange={(e) => setModal({ ...modal, numero: e.target.value })} data-testid="ad-numero" /></F>
            <F label="Data"><input type="date" className="input" value={modal.data} onChange={(e) => setModal({ ...modal, data: e.target.value })} data-testid="ad-data" /></F>
            <F label="Descrição"><input className="input" value={modal.descricao} onChange={(e) => setModal({ ...modal, descricao: e.target.value })} placeholder="Ex.: 1º Termo Aditivo — acréscimo de 25%" data-testid="ad-descricao" /></F>
            <F label="Novo prazo (opcional)"><input type="date" className="input" value={modal.prazo_novo} onChange={(e) => setModal({ ...modal, prazo_novo: e.target.value })} data-testid="ad-prazo" /></F>
          </div>
          <table className="pcp-table">
            <thead><tr><th style={{ width: "40%" }}>Item do contrato</th><th>Cód. / descrição (se item novo)</th><th style={{ textAlign: "right" }}>Qtd extra</th><th style={{ textAlign: "right" }}>Preço unit. aditivo</th><th /></tr></thead>
            <tbody>
              {modal.itens.map((l, i) => (
                <tr key={i} data-testid={`ad-linha-${i}`}>
                  <td>
                    <select className="select" value={l.contrato_item_id} onChange={(e) => escolherItem(i, e.target.value)} data-testid={`ad-item-${i}`}>
                      <option value="">— item NOVO (não existe na planilha) —</option>
                      {itens.map((ci) => <option key={ci.id} value={ci.id}>{ci.codigo ? ci.codigo + " · " : ""}{ci.descricao} · {ci.unidade} · {formatCurrency(ci.preco_unitario)}</option>)}
                    </select>
                  </td>
                  <td>{!l.contrato_item_id && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <input className="input" style={{ width: 70 }} placeholder="Cód." value={l.codigo} onChange={(e) => upd(i, { codigo: e.target.value })} data-testid={`ad-codigo-${i}`} />
                      <input className="input" placeholder="Descrição do item novo" value={l.descricao} onChange={(e) => upd(i, { descricao: e.target.value })} data-testid={`ad-desc-${i}`} />
                      <input className="input" style={{ width: 60 }} placeholder="un" value={l.unidade} onChange={(e) => upd(i, { unidade: e.target.value })} />
                    </div>
                  )}</td>
                  <td><input type="number" className="input" style={{ textAlign: "right" }} value={l.qtd} onChange={(e) => upd(i, { qtd: e.target.value })} data-testid={`ad-qtd-${i}`} /></td>
                  <td><input type="number" step="0.01" className="input" style={{ textAlign: "right" }} value={l.preco_unitario} onChange={(e) => upd(i, { preco_unitario: e.target.value })} data-testid={`ad-preco-${i}`} /></td>
                  <td><button className="btn btn-ghost btn-sm" onClick={() => setModal({ ...modal, itens: modal.itens.filter((_, k) => k !== i) })}><TrashSimple size={14} /></button></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={3} style={{ textAlign: "right", fontWeight: 700 }}>VALOR DO ADITIVO</td><td className="font-mono font-semibold" style={{ textAlign: "right" }} data-testid="ad-total">{formatCurrency(total)}</td><td /></tr></tfoot>
          </table>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
            <button className="btn btn-outline btn-sm" onClick={() => setModal({ ...modal, itens: [...modal.itens, novaLinha()] })} data-testid="ad-add-linha"><Plus size={14} weight="bold" /> Linha</button>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} data-testid="ad-salvar">Salvar aditivo</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function F({ label, children }) { return <div><div className="label">{label}</div>{children}</div>; }
